// Talen van het platform (§17.8).
//
// Brontaal is Engels: nieuwe teksten worden in het Engels toegevoegd en daarna
// vertaald, nooit andersom. De lijst met ondersteunde talen is bewust klein en
// staat hier als enige bron; de omgevingsvariabele SUPPORTED_LOCALES mag er een
// deelverzameling van kiezen, maar nooit een taal toevoegen die hier ontbreekt.

/** Brontaal van alle vertaalsleutels en user-facing brontekst. */
export const SOURCE_LOCALE = 'en' as const

/** Alle talen die het platform kent. Volgorde is betekenisvol: brontaal eerst. */
export const SUPPORTED_LOCALES = ['en', 'nl'] as const

export type Locale = (typeof SUPPORTED_LOCALES)[number]

/** Taalnaam in de eigen taal — gebruikt in taalkiezers. */
export const LOCALE_LABELS: Record<Locale, string> = {
  en: 'English',
  nl: 'Nederlands',
}

/** Type guard: is deze willekeurige string een door het platform ondersteunde taal? */
export function isLocale(value: string): value is Locale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value)
}

/** Eén ontleed onderdeel van een Accept-Language-header. */
interface AcceptLanguageEntry {
  /** Taalcode in kleine letters, bijvoorbeeld 'nl-nl' of '*'. */
  readonly tag: string
  /** Kwaliteitsfactor uit de header; standaard 1 (RFC 9110 §12.4.2). */
  readonly quality: number
  /** Oorspronkelijke positie in de header, gebruikt als tiebreak. */
  readonly order: number
}

/** Basistaal van een taalcode: 'nl-NL' → 'nl'. */
function baseLanguage(tag: string): string {
  const separator = tag.indexOf('-')
  return separator === -1 ? tag : tag.slice(0, separator)
}

/**
 * Ontleedt een Accept-Language-header naar entries, gesorteerd op aflopende
 * q-waarde. Bij gelijke q-waarde blijft de headervolgorde behouden, zodat de
 * uitkomst deterministisch is. Entries met q=0 zijn expliciet geweigerd door de
 * client en vallen af.
 */
function parseAcceptLanguage(header: string): AcceptLanguageEntry[] {
  const entries: AcceptLanguageEntry[] = []
  const parts = header.split(',')

  for (let index = 0; index < parts.length; index += 1) {
    const part = parts[index]
    if (part === undefined) {
      continue
    }

    const segments = part.split(';')
    const tag = (segments[0] ?? '').trim().toLowerCase()
    if (tag === '') {
      continue
    }

    let quality = 1
    for (let segmentIndex = 1; segmentIndex < segments.length; segmentIndex += 1) {
      const segment = segments[segmentIndex]
      if (segment === undefined) {
        continue
      }
      const separator = segment.indexOf('=')
      if (separator === -1) {
        continue
      }
      const key = segment.slice(0, separator).trim().toLowerCase()
      if (key !== 'q') {
        continue
      }
      const parsed = Number.parseFloat(segment.slice(separator + 1).trim())
      // Een onleesbare of buitenissige q-waarde valt terug op de standaard 1,
      // in plaats van de hele entry weg te gooien.
      quality = Number.isFinite(parsed) && parsed >= 0 && parsed <= 1 ? parsed : 1
    }

    if (quality === 0) {
      continue
    }

    entries.push({ tag, quality, order: index })
  }

  entries.sort((a, b) => (b.quality - a.quality !== 0 ? b.quality - a.quality : a.order - b.order))
  return entries
}

/**
 * Kiest de beste taal uit een Accept-Language-header.
 *
 * Regels:
 * - lege, ontbrekende of onbruikbare header → `fallback`;
 * - exacte match gaat voor match op basistaal ('nl-NL' matcht 'nl');
 * - '*' levert de fallback, of anders de eerste ondersteunde taal;
 * - bij gelijke q-waarde wint de taal die als eerste in de header staat.
 *
 * Dit is stap drie van de onderhandelingsvolgorde uit §17.8; de cookie-keuze en
 * de padprefix worden door de aanroeper afgehandeld.
 */
export function negotiateLocale(
  acceptLanguage: string | null | undefined,
  supported: readonly Locale[],
  fallback: Locale,
): Locale {
  if (supported.length === 0) {
    return fallback
  }
  if (acceptLanguage === null || acceptLanguage === undefined) {
    return fallback
  }

  const entries = parseAcceptLanguage(acceptLanguage)

  for (const entry of entries) {
    if (entry.tag === '*') {
      // Wildcard: de fallback heeft voorrang zolang die ondersteund is.
      return supported.includes(fallback) ? fallback : (supported[0] ?? fallback)
    }

    const exact = supported.find((locale) => locale.toLowerCase() === entry.tag)
    if (exact !== undefined) {
      return exact
    }

    const requestedBase = baseLanguage(entry.tag)
    const byBase = supported.find((locale) => baseLanguage(locale.toLowerCase()) === requestedBase)
    if (byBase !== undefined) {
      return byBase
    }
  }

  return fallback
}
